import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type UserInvitationDetails } from "../../../zodios/apiDefinitions/api/userManagement";
import { css } from "../../../../styled-system/css";
import { useClientTable } from "../../../components/table/tableFeatures";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { resetTable } from "../../../components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useUserInvitations } from "../api/useUserInvitations";
import Loading from "../../../components/core/blocks/Loading";
import { useResendUserInviteMutation } from "../api/useResendUserInviteMutation";
import { useDeleteUserInviteMutation } from "../api/useDeleteUserInviteMutation";

const columnHelper = createColumnBuilder<UserInvitationDetails>();

const UserInvitationTable = () => {
  const { t } = useTranslation(["user_management", "common"]);
  const userInvitationsQuery = useUserInvitations();
  const resendUserInvitationMutation = useResendUserInviteMutation();
  const deleteUserInvitationMutation = useDeleteUserInviteMutation();

  const invitationData = useMemo(() => {
    if (!userInvitationsQuery.data) {
      return [];
    }
    return userInvitationsQuery.data.invites;
  }, [userInvitationsQuery.data]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("invited_email", {
        header: t(($) => $.user_management["Invited Email"]),

        cell: (props) => <span className={css({ paddingY: "small" })}>{props.row.original.invited_email}</span>,
      }),
      columnHelper.accessor("activated", {
        header: t(($) => $.user_management["Status"]),
        cell: (props) => {
          const { activated, expired } = props.row.original;
          let displayStatus: string = t(($) => $.user_management["Pending"]);
          if (activated) displayStatus = t(($) => $.user_management["Accepted"]);
          else if (expired) displayStatus = t(($) => $.user_management["Expired"]);

          return <span className={css({ paddingY: "small" })}>{displayStatus}</span>;
        },
      }),
      columnHelper.accessor("role", {
        header: t(($) => $.user_management["Role"]),
        cell: (props) => {
          const { role, is_trellis_staff } = props.row.original;
          return <span>{is_trellis_staff ? t(($) => $.user_management["Staff"]) : role}</span>;
        },
      }),
      columnHelper.display({
        id: "resend",
        header: t(($) => $.user_management["Resend Invite"]),

        cell: (props) => {
          const { invited_email, activated } = props.row.original;
          if (activated) {
            return null;
          }
          return (
            <Button
              onPress={() => {
                resendUserInvitationMutation.mutate({
                  invite_email: invited_email,
                });
              }}
              size={"small"}
            >
              {t(($) => $.user_management["Resend"])}
            </Button>
          );
        },
      }),
      columnHelper.display({
        id: "deleteInvite",
        header: t(($) => $.user_management["Delete Invite"]),
        size: 200,
        cell: (props) => {
          const { activated, invited_email } = props.row.original;
          return !activated ?
              <Button
                onPress={() => {
                  deleteUserInvitationMutation.mutate({
                    invite_email: invited_email,
                  });
                }}
                size={"small"}
              >
                {t(($) => $.user_management["Delete"])}
              </Button>
            : null;
        },
      }),
    ];
  }, [deleteUserInvitationMutation, resendUserInvitationMutation, t]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "userInvitationsTable",
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
    },
  });

  const table = useClientTable({
    data: invitationData,
    columns,
    globalFilterFn: (row, columnId, filterValue) => {
      if (!filterValue) {
        return true;
      }

      const { invited_email } = row.original;
      const matchable = invited_email.toLowerCase();
      const searchable: string[] = filterValue.split(",");

      return searchable.some((searchTerm) => matchable.includes(searchTerm.trim().toLowerCase()));
    },
    initialState,
    state,
    ...setters,
  });

  return (
    <>
      {userInvitationsQuery.isLoading ?
        <Loading />
      : <>
          <HStack padding={"small"} fontSize={"large"}>
            <VStack flex="1" alignItems={"start"}>
              <HStack width={"100%"}>
                <Button
                  variant={"contentOnly"}
                  onPress={() => {
                    resetTable(table);
                  }}
                  fontSize={"xxlarge"}
                >
                  <MdRotateLeft />
                </Button>
                <SearchInput
                  aria-label={t(($) => $.common.actions.Search)}
                  isExpandable
                  css={{ flex: { base: "0 1 150px", md: "0 1 350px" } }}
                  value={state.globalFilter ?? ""}
                  onChange={(value) => {
                    setters.onGlobalFilterChange(value);
                  }}
                />
              </HStack>
            </VStack>
            <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
          </HStack>
          <Table table={table} />
        </>
      }
    </>
  );
};

export default UserInvitationTable;
